import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { testAPI, sandboxAPI, assessmentAPI } from '../../services/api'
import { testCompletionService } from '../../services/testCompletion'

export interface Question {
  id: number
  title?: string  // Made optional for MCQ questions
  description: string
  question_type: 'multiple_choice' | 'coding' | 'essay' | 'case_study' | 'aptitude' | 'fundamentals' | 'cloud_fundamentals' | 'presales_fundamentals' | 'english_communication' | 'aiml_assignment' | 'ai_fundamental'
  difficulty_level: string
  marks: number
  time_limit_minutes?: number
  options?: any[]
  language_restrictions?: string[]
  starter_code?: Record<string, string>
  test_cases?: any[]
  word_limit?: number
  order_index: number
  image?: string
  question_data?: any
}

export interface Assessment {
  id: number
  title: string
  description: string
  assessment_type: string
  duration_minutes: number
  total_marks: number
  questions?: Question[]
}

export interface TestSession {
  session_id: string
  status: string
  duration_minutes: number
  remaining_time_seconds?: number
  assessment_id?: number | string
  assessment_title?: string
  assessment_type?: string
  drive_id?: string
  drive_title?: string
  total_questions?: number
  start_time?: string
  created_at?: string
  proctoring_enabled?: boolean
  disable_tab_switch_warnings?: boolean
}

export interface QuestionResponse {
  question_id: number
  response_data: any
  is_submitted: boolean
  time_spent_seconds: number
  answer?: string
  is_correct?: boolean
  score?: number
}

interface TestState {
  currentSession: TestSession | null
  currentAssessment: Assessment | null
  questions: Question[]
  responses: Record<number, QuestionResponse>
  currentQuestionIndex: number
  isLoading: boolean
  error: string | null
  isSubmitting: boolean
}

const initialState: TestState = {
  currentSession: null,
  currentAssessment: null,
  questions: [],
  responses: {},
  currentQuestionIndex: 0,
  isLoading: false,
  error: null,
  isSubmitting: false,
}

// Async thunks
export const createSession = createAsyncThunk(
  'test/createSession',
  async (sessionData: { 
    assessment_id: number | string; 
    questions?: any[];
    duration_minutes?: number;
    assessment_type?: string;
    candidate_email?: string;
  }) => {
    const response = await testAPI.createSession(sessionData)
    return response.data
  }
)

export const startSession = createAsyncThunk(
  'test/startSession',
  async (sessionId: string) => {
    const response = await testAPI.startSession(sessionId)
    return response.data
  }
)

export const getSessionStatus = createAsyncThunk(
  'test/getSessionStatus',
  async (sessionId: string) => {
    const response = await testAPI.getSessionStatus(sessionId)
    return response.data
  }
)

export const submitSession = createAsyncThunk(
  'test/submitSession',
  async (input: string | { sessionId: string; reason: 'manual' | 'timer_expired' | 'tab_limit' }, { getState }) => {
    const sessionId = typeof input === 'string' ? input : input.sessionId
    const response = await testAPI.submitSession(sessionId, typeof input === 'string' ? 'manual' : input.reason)
    const state = (getState() as any)
    const testState = state.test
    const authState = state.auth
    
    // Mark assessment as completed in the test completion service
    if (testState.currentSession && testState.currentAssessment && authState?.user?.email) {
      const driveId = (testState.currentAssessment as any)?.driveId || (testState.currentSession as any)?.drive_id || ''
      const assessmentType = testState.currentSession.assessment_type || (testState.currentAssessment as any)?.assessment_type || ''
      const stableAssessmentId = (driveId && assessmentType)
        ? `${driveId}_${assessmentType}`
        : (testState.currentSession.assessment_id || (testState.currentAssessment as any)?.id)
      
      testCompletionService.markAssessmentCompleted({
        assessmentId: stableAssessmentId,
        assessmentTitle: testState.currentSession.assessment_title || testState.currentAssessment.title,
        assessmentType: testState.currentSession.assessment_type || testState.currentAssessment.assessment_type,
        driveId: (testState.currentAssessment as any)?.driveId || '',
        driveTitle: (testState.currentAssessment as any)?.driveTitle || '',
        sessionId: testState.currentSession.session_id,
        candidateEmail: authState.user.email,
        durationMinutes: testState.currentSession.duration_minutes || testState.currentAssessment.duration_minutes
      })
    }
    
    return response.data
  }
)

export const saveResponse = createAsyncThunk(
  'test/saveResponse',
  async ({ sessionId, questionId, responseData }: {
    sessionId: string
    questionId: number
    responseData: any
  }) => {
    const response = await testAPI.saveResponse(sessionId, questionId, responseData)
    return { questionId, responseData, ...response.data }
  }
)

export const executeCode = createAsyncThunk(
  'test/executeCode',
  async ({ sessionId, questionId, sourceCode, language }: {
    sessionId: string
    questionId: number
    sourceCode: string
    language: string
  }) => {
    const response = await sandboxAPI.executeCode({
      session_id: sessionId,
      question_id: questionId,
      source_code: sourceCode,
      language,
    })
    return response.data
  }
)

// New Assessment Responses System
export const saveQuestionResponse = createAsyncThunk(
  'test/saveQuestionResponse',
  async ({ assessmentId, candidateId, driveId, questionId, selectedOption }: {
    assessmentId: string
    candidateId: number
    driveId: number
    questionId: string
    selectedOption: string
  }) => {
    const response = await assessmentAPI.saveQuestionResponse(assessmentId, {
      candidate_id: candidateId,
      drive_id: driveId,
      question_id: questionId,
      selected_option: selectedOption
    })
    return response.data
  }
)

export const submitAssessment = createAsyncThunk(
  'test/submitAssessment',
  async ({ assessmentId, candidateId, driveId, remainingTimeSeconds, totalDurationMinutes, submissionReason }: {
    assessmentId: string
    candidateId: number
    driveId: number
    remainingTimeSeconds?: number
    totalDurationMinutes?: number
    submissionReason?: 'manual' | 'timer_expired' | 'tab_limit'
  }) => {
    const response = await assessmentAPI.submitAssessment(assessmentId, {
      candidate_id: candidateId,
      drive_id: driveId,
      remaining_time_seconds: remainingTimeSeconds,
      total_duration_minutes: totalDurationMinutes,
      submission_reason: submissionReason
    })
    return response.data
  }
)

export const getAssessmentResults = createAsyncThunk(
  'test/getAssessmentResults',
  async ({ assessmentId, candidateId, driveId }: {
    assessmentId: string
    candidateId: number
    driveId?: number
  }) => {
    const response = await assessmentAPI.getAssessmentResults(assessmentId, candidateId, driveId)
    return response.data
  }
)

const testSlice = createSlice({
  name: 'test',
  initialState,
  reducers: {
    setCurrentQuestion: (state, action: PayloadAction<number>) => {
      state.currentQuestionIndex = action.payload
    },
    updateResponse: (state, action: PayloadAction<{ questionId: number; responseData: any }>) => {
      if (!state.currentSession || ['submitted', 'completed', 'expired', 'cancelled'].includes(state.currentSession.status)) return
      const { questionId, responseData } = action.payload
      if (state.responses[questionId]) {
        state.responses[questionId].response_data = responseData
      } else {
        state.responses[questionId] = {
          question_id: questionId,
          response_data: responseData,
          is_submitted: false,
          time_spent_seconds: 0,
        }
      }
    },
    markQuestionVisited: (state, action: PayloadAction<number>) => {
      const questionId = action.payload
      if (!state.responses[questionId]) {
        state.responses[questionId] = {
          question_id: questionId,
          response_data: null,
          is_submitted: false,
          time_spent_seconds: 0,
        }
      }
    },
    clearTest: (state) => {
      state.currentSession = null
      state.currentAssessment = null
      state.questions = []
      state.responses = {}
      state.currentQuestionIndex = 0
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Create session
      .addCase(createSession.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentSession = action.payload
        state.questions = []
        state.responses = {}
        state.currentQuestionIndex = 0
        state.currentAssessment = null
      })
      .addCase(createSession.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to create session'
      })
      
      // Start session
      .addCase(startSession.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentSession = action.payload
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to start session'
      })
      
      // Get session status
      .addCase(getSessionStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getSessionStatus.fulfilled, (state, action) => {
        state.isLoading = false
        const payload = action.payload
        if (['submitted', 'completed', 'expired', 'cancelled'].includes(payload.status)) {
          state.questions = []
          state.responses = {}
          state.currentQuestionIndex = 0
          state.currentAssessment = null
          state.currentSession = { ...state.currentSession, ...payload }
          return
        }
        //console.log('getSessionStatus fulfilled with payload:', payload)
        //console.log('duration_minutes from payload:', payload.duration_minutes)
        
        // Update current session
        if (state.currentSession) {
          state.currentSession = { ...state.currentSession, ...payload }
        } else {
          // Create new session if it doesn't exist
          state.currentSession = {
            session_id: payload.session_id,
            status: payload.status,
            duration_minutes: payload.duration_minutes,
            remaining_time_seconds: payload.remaining_time_seconds,
            assessment_title: payload.assessment?.title || payload.assessment_title,
            assessment_type: payload.assessment?.assessment_type || payload.assessment_type,
            total_questions: payload.questions?.length || payload.total_questions,
            start_time: payload.start_time,
            created_at: payload.created_at
          }
        }
        
        //console.log('currentSession after update:', state.currentSession)
        
        // Handle questions if provided
        if (payload.questions && Array.isArray(payload.questions)) {
          //console.log('Setting questions in Redux store:', payload.questions)
          state.questions = payload.questions
          state.currentQuestionIndex = 0
          //console.log('Questions set, currentQuestionIndex:', state.currentQuestionIndex)
        } else {
          console.log('No questions in payload or not an array:', payload.questions)
        }
        
        // Handle assessment if provided
        if (payload.assessment) {
          state.currentAssessment = payload.assessment
        }
      })
      .addCase(getSessionStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to get session status'
        console.error('getSessionStatus rejected:', action.error)
      })
      
      // Submit session
      .addCase(submitSession.pending, (state) => {
        state.isSubmitting = true
        state.error = null
      })
      .addCase(submitSession.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.currentSession = action.payload
        state.questions = []
        state.responses = {}
        state.currentQuestionIndex = 0
        state.currentAssessment = null
      })
      .addCase(submitSession.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.error.message || 'Failed to submit session'
      })
      
      // Save response
      .addCase(saveResponse.fulfilled, (state, action) => {
        const { questionId } = action.payload
        if (state.responses[questionId]) {
          state.responses[questionId] = {
            ...state.responses[questionId],
            ...action.payload,
          }
        }
      })
      
      // Execute code
      .addCase(executeCode.pending, (state) => {
        state.isLoading = true
      })
      .addCase(executeCode.fulfilled, (state, action) => {
        state.isLoading = false
        // Update response with execution result
        const questionId = action.payload.question_id
        if (state.responses[questionId]) {
          state.responses[questionId].response_data = {
            ...state.responses[questionId].response_data,
            execution_result: action.payload,
          }
        }
      })
      .addCase(executeCode.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Code execution failed'
      })
      
      // New Assessment Responses System
      .addCase(saveQuestionResponse.pending, (state) => {
        state.isLoading = true
      })
      .addCase(saveQuestionResponse.fulfilled, (state, action) => {
        state.isLoading = false
        // Update the response in the store
        const { question_id, selected_option, is_correct, score } = action.payload.data
        if (state.responses[question_id]) {
          state.responses[question_id] = {
            ...state.responses[question_id],
            response_data: {
              ...state.responses[question_id].response_data,
              selected_option,
            },
            answer: selected_option,
            is_correct,
            score,
          }
        }
      })
      .addCase(saveQuestionResponse.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to save question response'
      })
      
      .addCase(submitAssessment.pending, (state) => {
        state.isSubmitting = true
      })
      .addCase(submitAssessment.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.questions = []
        state.responses = {}
        state.currentQuestionIndex = 0
        state.currentAssessment = null
        // Mark session as submitted
        if (state.currentSession) {
          state.currentSession.status = 'submitted'
        }
      })
      .addCase(submitAssessment.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.error.message || 'Failed to submit assessment'
      })
      
      .addCase(getAssessmentResults.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getAssessmentResults.fulfilled, (state, action) => {
        state.isLoading = false
        // Store assessment results in state if needed
      })
      .addCase(getAssessmentResults.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to get assessment results'
      })
  },
})

export const {
  setCurrentQuestion,
  updateResponse,
  markQuestionVisited,
  clearTest,
  clearError,
} = testSlice.actions

// Selectors
export const selectCurrentQuestion = (state: { test: TestState }): Question | null => {
  const { questions, currentQuestionIndex } = state.test
  // console.log('selectCurrentQuestion called:', { 
  //   questionsLength: questions.length, 
  //   currentQuestionIndex, 
  //   hasQuestions: questions.length > 0,
  //   indexInRange: currentQuestionIndex >= 0 && currentQuestionIndex < questions.length
  // })
  
  const result = questions.length > 0 && currentQuestionIndex >= 0 && currentQuestionIndex < questions.length 
    ? questions[currentQuestionIndex] 
    : null
    
  console.log('selectCurrentQuestion result:', result)
  return result
}

export default testSlice.reducer
